// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { MedplumClient } from '@medplum/core';
import type { Coding } from '@medplum/fhirtypes';

export const ICD10_VALUESET = 'http://hl7.org/fhir/sid/icd-10-cm/vs';
export const ICD10_SYSTEM = 'http://hl7.org/fhir/sid/icd-10-cm';

export function buildIcd10Concept(coding: Coding | undefined): { coding?: Coding[]; text?: string } | undefined {
  if (!coding) {
    return undefined;
  }

  const display = coding.display || coding.code;
  return {
    coding: [{ ...coding, system: ICD10_SYSTEM, display }],
    text: display,
  };
}

/** Converts user-entered ICD-10-CM codes to their canonical dotted form. */
export function normalizeIcd10Code(value: string): string {
  const compact = value.trim().replace(/\./g, '').toUpperCase();
  return compact.length > 3 ? `${compact.slice(0, 3)}.${compact.slice(3)}` : compact;
}

/** Resolves one exact ICD-10-CM code through the same ValueSet used by CodingInput. */
export async function lookupIcd10Code(medplum: MedplumClient, value: string): Promise<Coding | undefined> {
  const code = normalizeIcd10Code(value);
  if (!code) {
    return undefined;
  }

  const expanded = await medplum.valueSetExpand({ url: ICD10_VALUESET, filter: code, count: 20 });
  const contains: Coding[] = expanded.expansion?.contains ?? [];
  const match = contains.find(
    (coding) => coding.system === ICD10_SYSTEM && normalizeIcd10Code(coding.code ?? '') === code
  );
  const hasDescendant = contains.some(
    (coding) => coding.system === ICD10_SYSTEM && normalizeIcd10Code(coding.code ?? '').startsWith(`${code}.`)
  );

  return match && !hasDescendant ? { ...match, system: ICD10_SYSTEM, code } : undefined;
}
